<template>
  <div class="buy-credit-container">
    <div v-if="order.status === undefined" class="buy-credit-layout" :class="{ 'is-col': !isCN }">
      <template v-if="rightsDisplay?.rights">
        <FeatureInfo
          v-model:packageInfo="data.packageInfo"
          :feature-type="RIGHTS_FEATURE.credit_coin"
          :rights-name="currentRight?.rights_name ?? ''"
          :unit-price="feature_unit_price"
          :days="expiredDays"
          :pending="isCreating"
        />
        <template v-if="isCN">
          <div class="buy-credit-divider"></div>
          <PayQrcode
            v-model:pay-type="currentPayType"
            v-model:agreement-checked="agreementAccepted"
            :loading="isCreating"
            :qrcode="qrCode"
            :behavior="ORDER_BEHAVIOR.ADD_PURCHASE"
            :price-text="amount"
            :reorder="create"
          >
            <Consultant
              v-if="user?.rights_type"
              :right-type="user?.rights_type"
              class="buy-credit-consultant"
              :orderType="ORDER_TYPE.CREDITS"
              @transfer="onTransferClick"
              :transfer-disabled="false"
            />
          </PayQrcode>
        </template>
        <StripeOperation
          v-else
          :url="raw?.vendor_redirect_url"
          :loading="isCreating"
          @payed="handlePayed"
        >
        </StripeOperation>
      </template>
    </div>
    <div v-else-if="paySucceeded" class="buy-credit-succ">
      <PaySucc
        class="buy-credit-succ-content"
        :title="paySuccTitle"
        :desc="paySuccDesc"
        :skip-reload="true"
        @on-success="handlePayed"
      />
      <div class="bar buy-credit-bar"></div>
      <PaySuccessTip class="buy-credit-success-tip" />
    </div>
    <PayFail v-else class="buy-credit-fail-content" @back="repay()" />
  </div>
</template>

<script setup lang="ts" name="BuyRights">
import { ICreditCoinsPackage } from '@rendora/common/entities/rights';
import { getI18nService } from '@rendora/common/services/i18n';
import { useRightsStore } from '@rendora/common/store/rights';
import { useUserStore } from '@rendora/common/store/user';
import { BUY_TYPE, ORDER_BEHAVIOR, ORDER_TYPE, RIGHTS_FEATURE } from '@rendora/common/types/enums';
import { getGlobalConfig } from '@rendora/common/utils/config';
import { formatPrice } from '@rendora/common/utils/transform';
import { computed, ref } from 'vue';
import { UseOrderInput } from '../../composables/types';
import useOrder from '../../composables/useOrder';
import useOverseasOrder from '../../composables/useOverseasOrder';
import Consultant from '../Consultant.vue';
import PayFail from '../PayFail.vue';
import PayQrcode from '../PayQrCode.vue';
import PaySucc from '../PaySuccess.vue';
import PaySuccessTip from '../PaySuccessTip.vue';
import StripeOperation from '../StripeOperation.vue';
import FeatureInfo from './FeatureInfo.vue';

const i18nService = getI18nService();
const $t = i18nService.t;

const isCN = getGlobalConfig().isCN;

const feature = {
  name: $t('yan-bi'),
  desc: [
    $t(
      'jia-gou-de-yan-bi-gou-mai-cheng-gong-hou-li-ji-sheng-xiao-yi-jing-sheng-xiao-bu-ke-tui-kuan',
    ),
    $t('tao-can-you-xiao-qi-nei-ke-yong-chang-qi-bao-liu'),
  ],
  priceKey: 'add_credit_coin',
};

const { user } = useUserStore();
const { rightsDisplay } = useRightsStore();
const expiredDays = computed(() => user?.rights_record.credit_coin_time);

const onTransferClick = () => {
  create(true);
};

const currentRight = computed(() =>
  rightsDisplay?.rights?.find(v => v.rights_type === user?.rights_type),
);

const addPrice = computed(() => {
  const addPrice = currentRight.value?.rights_by_time.find(
    v => v.id === user?.rights_id,
  )?.add_price;
  return addPrice;
});

const data = ref<{
  behavior: ORDER_BEHAVIOR;
  rights_type?: number;
  order_type: ORDER_TYPE;
  parent_order_id?: number;
  packageInfo?: ICreditCoinsPackage;
}>({
  behavior: ORDER_BEHAVIOR.BUY,
  rights_type: user?.rights_type,
  order_type: ORDER_TYPE.CREDITS,
  parent_order_id: user?.current_order_id,
});

// 权益单价
const feature_unit_price = computed(() => {
  if (!addPrice.value) {
    return 0;
  }
  return addPrice.value?.add_credit_coin ?? 0;
});

const amount = computed(() => data.value.packageInfo?.price || 0);
const yuanFormat = computed(() => formatPrice(amount.value).string);

const paySuccTitle = computed(() => yuanFormat.value);
const paySuccDesc = computed(() => {
  const rightsName = user?.rights_name;
  // const rightsName = rightsDisplay?.rights.find(v => v.id === user?)?.rights_name ?? '';
  const totalCoins = data.value.packageInfo?.total_credit_coins ?? 0;
  const featureName = feature.name;
  const amountText = yuanFormat.value;
  return $t(
    'rightsname-totalcoins-ge-featurename-jia-gou-cheng-gong-cheng-gong-zhi-fu-amounttext',
    [rightsName, totalCoins, featureName, amountText],
  );
});

const orderBody = computed(() => {
  return <UseOrderInput>{
    behavior: ORDER_BEHAVIOR.ADD_PURCHASE,
    orderType: ORDER_TYPE.CREDITS,
    creditCoinsPackageId: data.value.packageInfo?.id,
    buyType: BUY_TYPE.PAYMENT,
    packageInfo: data.value.packageInfo,
    parentOrderId: data.value.parent_order_id,
  };
});

// 区分国内和海外订单处理
const orderResult = isCN ? useOrder(orderBody) : useOverseasOrder(orderBody);

const {
  order,
  repay,
  isCreating,
  create,
  qrCode,
  raw,
  agreementAccepted,
  currentPayType,
  paySucceeded,
} = orderResult;

const handlePayed = () => location.reload();
</script>

<style scoped>
.buy-credit-container {
  width: 100%;
  display: grid;
  place-items: center;
  border-radius: var(--x-radius-large);
  background-color: var(--x-back-1);
  min-height: 300px;
}

.buy-credit-layout {
  display: flex;
}

.buy-credit-layout.is-col {
  flex-direction: column;
}

.buy-credit-divider {
  width: 1px;
  background-color: #f1f1f1;
}

.buy-credit-consultant {
  margin-top: 20px;
}

.buy-credit-succ {
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.buy-credit-succ-content {
  flex: 1 1 auto;
  padding-top: 173px;
  padding-bottom: 173px;
}

.buy-credit-bar {
  width: 92%;
  margin-left: auto;
  margin-right: auto;
  height: 1px;
  background-color: var(--x-back-3);
}

.buy-credit-success-tip {
  margin-top: 28px;
  margin-bottom: 28px;
}

.buy-credit-fail-content {
  padding-top: 173px;
  padding-bottom: 173px;
}
</style>
